import { detectClient, getDeviceId } from "./client-info";
import { prepareDecoder, setWasmUrl } from "./decoder";
import { setAllowedSymbologies } from "./entitlements";
import { activateLicense } from "./license";
import { setWorkerFactory } from "./runtime";
import { setActiveReporter, UsageReporter } from "./usage";

export { ScannerView } from "./scanner";
export { BarqScanLicenseError } from "./license";
export { enhance } from "./enhance";
export { locateBarcode, locateBarcodes } from "./locate";
export type { NormalizedRect } from "./locate";
export { parseGS1 } from "./gs1";
export type { GS1Element } from "./gs1";
export { createPipelineState, runPipeline } from "./pipeline";
export type { PipelineOutcome } from "./pipeline";
export type { ScannerOptions, ScanEvent, ScannerStats } from "./scanner";
export type { Symbology } from "./formats";
export type { DecodeResult } from "./decoder";
export type { CameraInfo } from "./camera";

/** Hosted BarqScan licensing portal. Self-hosters override via licenseServer. */
const DEFAULT_LICENSE_SERVER = "https://portal.barqscan.com";

/** Ed25519 public key matching the production portal's signing secret. */
const DEFAULT_PUBLIC_KEY = "ivdC+QBMYvWeoJtWMJmZMwzi50mStymb1PpB0ydbvOU=";

export interface ConfigureOptions {
  /** Self-hosted zxing_reader.wasm URL (bundler asset). Defaults to CDN. */
  wasmUrl?: string;
  /** License portal origin. Defaults to the hosted BarqScan portal. */
  licenseServer?: string;
  /** Override the token verification public key (self-hosted portals). */
  publicKey?: string;
  /** Advanced: override the hostname used for domain locking (SSR/tests). */
  hostname?: string;
  /** Advanced: injectable fetch for tests. */
  fetchFn?: typeof fetch;
  /**
   * Decode-worker factory (e.g. Vite: `() => new MyWorker()` from a
   * `?worker` import). Defaults to auto-resolving the sibling dist worker;
   * pass null to force inline decoding.
   */
  createWorker?: (() => Worker) | null;
}

/**
 * Activate the license for this page, then warm up the scanning engine.
 * Rejects with BarqScanLicenseError when the key/domain is not licensed.
 * On localhost and *.local hostnames this always succeeds in dev mode.
 */
export async function configure(
  licenseKey: string,
  opts: ConfigureOptions = {},
): Promise<{ plan: string }> {
  const hostname =
    opts.hostname ?? globalThis.location?.hostname ?? "localhost";
  const licenseServer = opts.licenseServer ?? DEFAULT_LICENSE_SERVER;

  if (opts.wasmUrl) setWasmUrl(opts.wasmUrl);
  if (opts.createWorker !== undefined) setWorkerFactory(opts.createWorker);

  const grant = await activateLicense({
    key: licenseKey,
    hostname,
    licenseServer,
    publicKey: opts.publicKey ?? DEFAULT_PUBLIC_KEY,
    storage: typeof localStorage !== "undefined" ? localStorage : undefined,
    fetchFn: opts.fetchFn,
  });
  const plan = grant.plan;
  setAllowedSymbologies(grant.symbologies ?? null);

  const storage = typeof localStorage !== "undefined" ? localStorage : undefined;
  const client = await detectClient();
  const reporter = new UsageReporter({
    key: licenseKey,
    domain: hostname,
    licenseServer: plan === "dev" ? "" : licenseServer,
    deviceId: getDeviceId(storage),
    platform: client.platform,
    model: client.model,
  });
  reporter.start();
  setActiveReporter(reporter);

  await prepareDecoder();
  return { plan };
}
